import React from "react"
import { ReactSVG } from "react-svg"

import { graphql, Link } from "gatsby"
import { CoverArticleFieldsFragment } from "../../../../graphql-types"

import * as styles from "./cover.module.scss"
import { dateFormat } from "../../../components/common/functions/functions"
import { useLocale } from "../../../context/locale-context"
import useWindowWidth from "../../../hooks/use-window-width"
import linkedin from "../../../../static/vectors/linkedin.svg"
import twitter from "../../../../static/vectors/twitter.svg"

type CoverProps = {
  data: any
  fields: CoverArticleFieldsFragment
  searchRequestSlug: string
}

const Cover: React.FC<CoverProps> = ({ data, fields, searchRequestSlug }) => {
  const { locale } = useLocale()
  const isMobile = useWindowWidth() <= 825
  const isOnBrowser = typeof window !== "undefined"

  const displaySocialContent = (className?): JSX.Element => {
    return (
      <ul className={className}>
        <a
          className={styles.socialButton}
          href={`https://twitter.com/intent/tweet?url=${isOnBrowser && window.location.href}&text=${data.title}`}
          target="_blank"
          aria-label="Twitter"
          title="Share on Twitter"
        >
          <li>
            <ReactSVG src={twitter} />
          </li>
        </a>
        <a
          className={styles.socialButton}
          href={`https://www.linkedin.com/sharing/share-offsite/?mini=true&url=${
            isOnBrowser && window.location.href
          }&summary=${data.title}`}
          target="_blank"
          aria-label="LinkedIn"
          title="Share on LinkedIn"
        >
          <li>
            <ReactSVG src={linkedin} />
          </li>
        </a>
      </ul>
    )
  }

  const displayTags = (): JSX.Element => {
    return (
      <span className={styles.sectionContent}>
        <strong>
          {data.tags.map((tag, index) => {
              return (
                <div key={index} className={styles.tag}>
                  <Link to={`/${searchRequestSlug}?query=${tag.label}`} target="_blank">
                    {tag.label}
                  </Link>
              </div>
            )
          })}
        </strong>
      </span>
    )
  }

  const displayImg = (image, classes?): JSX.Element => {
    if (!image) return
    return image.format === "svg" ? (
      <ReactSVG src={image.url} className={classes} />
    ) : (
      <img src={image.url} alt={image.alt || ""} className={classes} />
    )
  }

  const displayPartners = (): JSX.Element => {
    return (
        <ul className={styles.sectionContent} style={data.partners.length > 4 ? {display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "10px"} : {display: "flex", flexWrap: "wrap", gap: "10px"}}>
          {
            data.partners.map((partner, index) => {
              return (
                <li key={index} className={styles.partnerButton}>
                  <a href={partner.link} target="_blank" className={styles.partnerLink} aria-label="Partner link">
                    {displayImg(partner.logo, styles.imageW100)}
                  </a>
                </li>
              )
            })
          }
        </ul>
    )
  }

  return (
    <section className={styles.v2Container}>
        <div className={styles.v2TitleContainer}>
          <p className={styles.v2Category}>{data.category.label}</p>
          <h1 className={styles.v2Title}>{data.articleTitle}</h1>
        </div>
        <div className={styles.v2ThumbnailContainer}>
          <div
            className={styles.v2ImageContainer}
            style={{ backgroundImage: `url(${data.image.url})` }}
          >
          </div>
          <div className={styles.v2ThumbnailTextContainer}>
            <p className={styles.v2ImageDescription}>{data.description}</p>
          </div>
        </div>
        <div className={styles.v2DetailsContainer}>
          <div className={styles.v2PartnersContainer}>
            <p className={styles.v2PartnersTitle}>
              {fields.partners}
            </p>
            {displayPartners()}
          </div>
          <div className={styles.v2PostedContainer}>
            <p className={styles.v2PostedTitle}>
              {fields.posted}
            </p>
            <p className={styles.v2PostedDate}>
              {dateFormat(data.date, locale)}
            </p>
          </div>
          <div className={styles.v2TagsContainer} style={{ maxWidth: isMobile ? "100%" : "300px" }}>
            <p className={styles.v2TagsTitle}>
              {fields.tags}
            </p>
            <div className={styles.v2Tags}>
              {displayTags()}
            </div>
          </div>
          <div className={styles.v2ShareContainer}>
            <p className={styles.v2ShareTitle}>
              {fields.shareOn}
            </p>
            <div>
              {displaySocialContent(styles.v2ShareContent)}
            </div>
          </div>
        </div>
      </section>
    )
  }

export const fragment = graphql`
  fragment CoverArticleFields on DatoCmsFieldsArticlePage {
    shareOn
    posted
    wordsBy
    tags
    partners
  }
`

export default Cover
